import React from "react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const CHART_COLORS = ["#6366f1", "#f59e0b", "#10b981", "#ec4899", "#22d3ee", "#a855f7", "#f97316"];

/**
 * Renders a slide preview based on its layout. Uses the active template theme.
 *  aspect = base 16:9
 */
export default function SlidePreview({ slide, theme, scale = 1, editable = false, onEdit }) {
  const bg = theme.bg;
  const fg = theme.fg;
  const accent = theme.accent;
  const font = slide.font_override || theme.font;
  const style = {
    background: bg,
    color: fg,
    fontFamily: `'${font}', system-ui, sans-serif`,
    width: 960 * scale,
    height: 540 * scale,
    padding: `${48 * scale}px ${64 * scale}px`,
  };

  const layout = slide.layout || "bullets";

  return (
    <div className="relative rounded-md shadow-lg overflow-hidden flex flex-col" style={style} data-testid="slide-canvas">
      {layout !== "title" && layout !== "closing" && (
        <Heading text={slide.heading} sub={slide.subheading} fg={fg} accent={accent} scale={scale} editable={editable} onChange={(v) => onEdit?.({ heading: v })} onChangeSub={(v) => onEdit?.({ subheading: v })} />
      )}

      <div className="flex-1 min-h-0 mt-4" style={{ fontSize: 20 * scale }}>
        {layout === "title" && <TitleLayout slide={slide} fg={fg} accent={accent} scale={scale} editable={editable} onEdit={onEdit} />}
        {layout === "closing" && <ClosingLayout slide={slide} fg={fg} accent={accent} scale={scale} editable={editable} onEdit={onEdit} />}
        {layout === "bullets" && <BulletsLayout slide={slide} fg={fg} accent={accent} scale={scale} editable={editable} onEdit={onEdit} />}
        {layout === "stat" && <StatLayout slide={slide} accent={accent} fg={fg} scale={scale} />}
        {layout === "quote" && <QuoteLayout slide={slide} accent={accent} fg={fg} scale={scale} editable={editable} onEdit={onEdit} />}
        {layout === "two_column" && <TwoColumnLayout slide={slide} accent={accent} fg={fg} scale={scale} />}
        {layout === "comparison" && <TwoColumnLayout slide={slide} accent={accent} fg={fg} scale={scale} comparison />}
        {layout === "chart" && <ChartLayout slide={slide} accent={accent} fg={fg} scale={scale} />}
        {layout === "infographic" && <InfographicLayout slide={slide} accent={accent} fg={fg} scale={scale} />}
        {layout === "timeline" && <TimelineLayout slide={slide} accent={accent} fg={fg} scale={scale} />}
        {layout === "team" && <TeamLayout slide={slide} accent={accent} fg={fg} scale={scale} />}
      </div>

      {/* Accent bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: accent }} />
    </div>
  );
}

function EditableText({ value, onChange, style, className, placeholder, tag = "div", editable }) {
  const Tag = tag;
  const ref = React.useRef(null);
  return editable ? (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onChange?.(e.currentTarget.innerText)}
      className={`${className || ""} outline-none focus:ring-2 focus:ring-indigo-400 rounded`}
      style={style}
      data-placeholder={placeholder}
    >{value}</Tag>
  ) : <Tag className={className} style={style}>{value || placeholder}</Tag>;
}

function Heading({ text, sub, fg, accent, scale, editable, onChange, onChangeSub }) {
  return (
    <div>
      <EditableText tag="h1" value={text} onChange={onChange} editable={editable}
        className="font-bold leading-tight" style={{ fontSize: 40 * scale, color: fg }} placeholder="Heading" />
      {(sub || editable) && (
        <EditableText tag="div" value={sub} onChange={onChangeSub} editable={editable}
          className="mt-1 font-medium" style={{ fontSize: 18 * scale, color: accent }} placeholder="Subheading" />
      )}
    </div>
  );
}

function TitleLayout({ slide, fg, accent, scale, editable, onEdit }) {
  return (
    <div className="h-full flex flex-col justify-center items-center text-center">
      <EditableText tag="h1" value={slide.heading} onChange={(v) => onEdit?.({ heading: v })} editable={editable}
        className="font-black leading-tight" style={{ fontSize: 68 * scale, color: fg }} placeholder="Presentation Title" />
      <EditableText tag="div" value={slide.subheading} onChange={(v) => onEdit?.({ subheading: v })} editable={editable}
        className="mt-6 font-medium tracking-wide uppercase" style={{ fontSize: 20 * scale, color: accent, letterSpacing: "0.2em" }} placeholder="Subtitle" />
    </div>
  );
}

function ClosingLayout({ slide, fg, accent, scale, editable, onEdit }) {
  return (
    <div className="h-full flex flex-col justify-center items-center text-center">
      <EditableText tag="h1" value={slide.heading || "Thank you"} onChange={(v) => onEdit?.({ heading: v })} editable={editable}
        className="font-black" style={{ fontSize: 72 * scale, color: fg }} placeholder="Thank you" />
      <EditableText tag="div" value={slide.subheading} onChange={(v) => onEdit?.({ subheading: v })} editable={editable}
        className="mt-4" style={{ fontSize: 22 * scale, color: accent }} placeholder="Questions? Let's chat." />
    </div>
  );
}

function BulletsLayout({ slide, fg, accent, scale, editable, onEdit }) {
  const bullets = slide.bullets || [];
  const updateBullet = (idx, v) => {
    const next = [...bullets]; next[idx] = v;
    onEdit?.({ bullets: next });
  };
  return (
    <ul className="space-y-3">
      {bullets.map((b, i) => (
        <li key={i} className="flex items-start gap-3" style={{ color: fg, fontSize: 20 * scale }}>
          <span className="mt-2 w-2 h-2 rounded-full shrink-0" style={{ background: accent }} />
          <EditableText tag="span" value={b} onChange={(v) => updateBullet(i, v)} editable={editable}
            className="flex-1" style={{ color: fg }} placeholder="Point" />
        </li>
      ))}
    </ul>
  );
}

function StatLayout({ slide, accent, fg, scale }) {
  const stats = (slide.stats || []).slice(0, 4);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 h-full items-center">
      {stats.map((s, i) => (
        <div key={i} className="text-center">
          <div className="font-black" style={{ fontSize: 56 * scale, color: accent }}>{s.value}{s.unit}</div>
          <div className="mt-2 font-medium" style={{ fontSize: 16 * scale, color: fg }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

function QuoteLayout({ slide, accent, fg, scale, editable, onEdit }) {
  return (
    <div className="h-full flex flex-col justify-center">
      <div className="font-black" style={{ fontSize: 96 * scale, color: accent, lineHeight: 0.7 }}>“</div>
      <EditableText tag="div" value={slide.quote} onChange={(v) => onEdit?.({ quote: v })} editable={editable}
        className="italic leading-snug -mt-4" style={{ fontSize: 30 * scale, color: fg }} placeholder="Meaningful quote here" />
      <EditableText tag="div" value={`— ${slide.author || ""}`} onChange={(v) => onEdit?.({ author: v.replace(/^—\s*/, "") })} editable={editable}
        className="mt-4 font-semibold" style={{ fontSize: 16 * scale, color: accent }} placeholder="— Author" />
    </div>
  );
}

function TwoColumnLayout({ slide, accent, fg, scale, comparison }) {
  const cols = (slide.columns || []).slice(0, 2);
  return (
    <div className="grid grid-cols-2 gap-6 h-full">
      {cols.map((c, i) => (
        <div key={i} className={`p-6 rounded-lg`} style={{ background: comparison ? `${accent}18` : "transparent", border: comparison ? `1px solid ${accent}55` : "none" }}>
          <div className="font-bold" style={{ fontSize: 22 * scale, color: accent }}>{c.heading}</div>
          <div className="mt-3" style={{ fontSize: 16 * scale, color: fg }}>{c.body}</div>
        </div>
      ))}
    </div>
  );
}

function ChartLayout({ slide, accent, fg, scale }) {
  const chart = slide.chart || { kind: "bar", data: [] };
  const data = chart.data || [];
  const kind = chart.kind || "bar";
  return (
    <div className="w-full h-full" style={{ minHeight: 260 * scale }}>
      <ResponsiveContainer width="100%" height="100%">
        {kind === "bar" ? (
          <BarChart data={data}>
            <XAxis dataKey="name" stroke={fg} fontSize={12} />
            <YAxis stroke={fg} fontSize={12} />
            <Tooltip />
            <Bar dataKey="value" fill={accent} radius={[6,6,0,0]} />
          </BarChart>
        ) : kind === "line" ? (
          <LineChart data={data}>
            <XAxis dataKey="name" stroke={fg} fontSize={12} />
            <YAxis stroke={fg} fontSize={12} />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke={accent} strokeWidth={3} dot={{ fill: accent }} />
          </LineChart>
        ) : kind === "area" ? (
          <AreaChart data={data}>
            <XAxis dataKey="name" stroke={fg} fontSize={12} />
            <YAxis stroke={fg} fontSize={12} />
            <Tooltip />
            <Area type="monotone" dataKey="value" stroke={accent} fill={`${accent}55`} />
          </AreaChart>
        ) : (
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" outerRadius={110 * scale} label>
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Legend />
            <Tooltip />
          </PieChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

function InfographicLayout({ slide, accent, fg, scale }) {
  const stats = slide.stats || [];
  return (
    <div className="grid grid-cols-3 gap-4 h-full">
      {stats.slice(0, 6).map((s, i) => (
        <div key={i} className="rounded-lg p-4 flex flex-col justify-center items-center text-center"
             style={{ background: `${CHART_COLORS[i % CHART_COLORS.length]}22`, border: `2px solid ${CHART_COLORS[i % CHART_COLORS.length]}` }}>
          <div className="font-black" style={{ fontSize: 40 * scale, color: CHART_COLORS[i % CHART_COLORS.length] }}>{s.value}{s.unit}</div>
          <div className="mt-1 font-medium" style={{ fontSize: 13 * scale, color: fg }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

function TimelineLayout({ slide, accent, fg, scale }) {
  const items = (slide.timeline || []).slice(0, 6);
  return (
    <div className="relative">
      <div className="absolute top-6 left-0 right-0 h-0.5" style={{ background: `${accent}55` }} />
      <div className="grid grid-cols-6 gap-2">
        {items.map((it, i) => (
          <div key={i} className="flex flex-col items-center relative">
            <div className="w-4 h-4 rounded-full z-10" style={{ background: accent, boxShadow: `0 0 0 4px ${accent}33` }} />
            <div className="font-bold mt-4" style={{ fontSize: 14 * scale, color: accent }}>{it.date}</div>
            <div className="font-semibold mt-1 text-center" style={{ fontSize: 15 * scale, color: fg }}>{it.title}</div>
            <div className="text-center mt-1" style={{ fontSize: 11 * scale, color: fg, opacity: 0.7 }}>{it.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TeamLayout({ slide, accent, fg, scale }) {
  const members = slide.bullets || [];
  return (
    <div className="grid grid-cols-4 gap-4">
      {members.slice(0, 8).map((m, i) => (
        <div key={i} className="text-center">
          <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center font-bold"
               style={{ background: `${accent}30`, color: accent, fontSize: 20 * scale }}>
            {(m || "?").split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase()}
          </div>
          <div className="mt-2 font-medium" style={{ fontSize: 13 * scale, color: fg }}>{m}</div>
        </div>
      ))}
    </div>
  );
}
